import { Document, Image, Link, Page, Text, View } from "@react-pdf/renderer";
import dayjs from "dayjs";
import i18next, { t } from "i18next";

import {
  formatPhoneNumber,
  stripPhoneNumberFormatting,
  formatDateForPDF,
} from "@/common/utils";
import {
  approvedByText,
  creatorText,
} from "@/components/alerts/add/DisplayAlert/utils";
import {
  entityPhotos,
  photoLetter,
} from "@/components/alerts/entities/entityPhotos";
import { toEntityRows } from "@/components/alerts/entities/entityRows";
import {
  getAddressClassification,
  getAddressDescription,
} from "@/components/alerts/utils";
import { IAlertProperties, ICreator, MediaBlob } from "@/types";
import { IEvidenceFile } from "@/types/typesAlerts";

import AttachmentTags from "./AttachmentTags";
import PDFEntityList from "./PDFEntityList";
import {
  ADDITIONAL_IMAGE_ROW_HEIGHT,
  dlPDFDetailsStyles,
  dlPDFFooterStyles,
  dlPDFHeaderStyles,
  dlPDFImageStyles,
  dlPDFResolutionStyles,
} from "./ReactPDFStyling";
import { TAG_TITLE_MAX_LENGTH, truncateTagTitle } from "./tagTitle";

const TAG_DESCRIPTION_MAX_COLUMNS = 3;

interface generateAlertPDFProps {
  alertProperties: IAlertProperties;
  policeLogo: any;
  evidenceArray: MediaBlob[];
  evidenceBlobUrls: Map<string, string>;
  resolutionBlobUrls: Map<string, string>;
  issuedDate: string | undefined;
  currentUser: ICreator | null;
  getCrimeLabel: (key: string) => string;
}
/**
 * Generates a PDF of the alert
 * @param alertProperties
 * @param policeLogo
 * @param evidenceArray
 * @param issuedTime
 * @param currentUser
 * @returns PDF of the alert
 */
export function generateAlertPDF({
  alertProperties,
  policeLogo,
  evidenceArray,
  evidenceBlobUrls,
  resolutionBlobUrls,
  issuedDate: issuedTime,
  currentUser,
  getCrimeLabel,
}: generateAlertPDFProps) {
  const {
    policeService,
    type,
    associatedCrimes,
    occurrenceNumbers,
    incidentTimes,
    description,
    operationalArea,
    approvedBy,
    approvedByBadge,
    classification,
    contacts,
    bulletinId: alertId,
    tagDict,
    entities,
    evidence_files,
    creator,
    resolutions,
  } = alertProperties;
  const locale = i18next.language;

  // Only published resolutions are shown publicly; backend returns them newest first
  const publishedResolutions = (resolutions ?? []).filter((r) => r.published);
  // The bulletin is "resolved" only when its most recent published resolution is
  // closed — a later comment that does not close it reopens the bulletin. Latest
  // is determined by time_updated, matching BulletinCard.
  const latestPublishedResolution = [...publishedResolutions].sort(
    (a, b) =>
      new Date(b.time_updated).getTime() - new Date(a.time_updated).getTime(),
  )[0];
  const resolvedResolution = latestPublishedResolution?.resolved
    ? latestPublishedResolution
    : undefined;
  const resolutionLabel = (r: { reason: string; other_reason: string }) =>
    r.reason !== "Other" ? r.reason : r.other_reason;

  const incidentTimeText = (incidentTime: {
    start_date: string;
    end_date: string;
    time_type: string;
  }) => {
    if (incidentTime.time_type === "range") {
      return `${formatDateForPDF(incidentTime.start_date, locale)} - ${formatDateForPDF(incidentTime.end_date, locale)}`;
    } else if (incidentTime.time_type === "unknown") {
      return t("bulletin:time_unknown");
    } else if (incidentTime.time_type === "exact") {
      return `${formatDateForPDF(incidentTime.start_date, locale)}`;
    } else if (incidentTime.time_type === "not_applicable") {
      return t("bulletin:not_applicable");
    } else if (incidentTime.time_type === "time_unknown") {
      return formatDateForPDF(incidentTime.start_date, locale, true);
    }
  };

  const creatorToUse = creator ? creator : currentUser;

  /**
   * First three attachments are featured under the header; the rest follow the
   * bulletin content in the same <Page> so they fill the space left on it. A
   * separate <Page> would force a new sheet and waste that space.
   */
  const featuredImages = evidenceArray.slice(0, 3);
  const additionalImages = evidenceArray.slice(3);
  /**
   * @param imageCount
   * @returns styling for the image based on the number of images on the page
   */
  const calculateImageDimensions = (imageCount: number) => {
    const result: {
      width: string;
      height: string;
    } = {} as any;
    const maxWidthPerImage = 95 / imageCount;

    result.width = `${maxWidthPerImage}%`;
    return result;
  };

  const currentDate = dayjs(new Date()).format("YYYY-MM-DD HH:mm");

  // Map of attachment to tags
  const tagDictValues = Object.values(tagDict);
  const tagDictWithDescription = tagDictValues.filter(
    (tag) => tag.description !== "",
  );
  const tagDictWithoutDescription = tagDictValues.filter(
    (tag) => tag.description === "",
  );

  /** One tag spans the row, two share it, three or more sit in thirds. */
  const tagDescriptionColumns = Math.min(
    Math.max(tagDictWithDescription.length, 1),
    TAG_DESCRIPTION_MAX_COLUMNS,
  );

  /**
   * `TAG_TITLE_MAX_LENGTH` is what fits a one-third column, so a wider cell
   * takes proportionally more before it needs an ellipsis.
   */
  const tagDescriptionTitleLength =
    (TAG_TITLE_MAX_LENGTH * TAG_DESCRIPTION_MAX_COLUMNS) /
    tagDescriptionColumns;
  /**
   * Entities replaced tags: nothing writes tags any more, so a current bulletin's `tagDict` is
   * empty and printing it is what dropped the suspect descriptions. The tag grid stays only for a
   * bulletin that has tags and no entities.
   */
  const entityT = i18next.getFixedT(null, "bulletin/entities");
  const entityRows = toEntityRows(entities ?? [], entityT);
  const hasEntities = entityRows.length > 0;

  // Gallery order, as `DisplayAlert` sorts it, so a photo letter here is the letter on screen.
  const medias = (Object.values(evidence_files ?? {}) as IEvidenceFile[]).sort(
    (a, b) => a.sort_ordinal - b.sort_ordinal,
  );
  const photosByEntity = entityPhotos(medias);

  /** What is printed under each attachment, keyed by filename. */
  const attachmentLabels: { [filename: string]: string[] } = {};
  const attachmentLetters: { [filename: string]: string } = {};
  if (hasEntities) {
    medias.forEach((media, index) => {
      attachmentLetters[media.filename] = photoLetter(index);
      attachmentLabels[media.filename] = (media.entity_ids ?? [])
        .map((id) => entityRows.find((row) => row.id === id)?.heading)
        .filter((heading): heading is string => !!heading);
    });
  } else {
    for (const tag of tagDictValues) {
      for (const attachment of tag.attachments) {
        (attachmentLabels[attachment] ??= []).push(tag.title);
      }
    }
  }

  const alertUrl =
    window.location.origin + `/rubialert/alerts/${alertId}/single`;

  const renderAddresses = () => {
    return alertProperties.rawAddresses.map((address, index) => {
      const classification =
        getAddressClassification(address) || "Incident Address";
      const addressNumber =
        alertProperties.rawAddresses.filter(
          (addr) => getAddressClassification(addr) === classification,
        ).length > 1
          ? ` ${index + 1}`
          : "";
      const description = getAddressDescription(address);

      return (
        <Text style={dlPDFDetailsStyles.address} key={index.toString()}>
          {classification + addressNumber}: {description}
        </Text>
      );
    });
  };

  const MyDocument = () => (
    <Document>
      <Page size="A4" wrap style={{ paddingTop: 16, paddingBottom: 40 }}>
        <View style={dlPDFHeaderStyles.header_container}>
          <View style={dlPDFHeaderStyles.logo_container}>
            <Image src={policeLogo} style={dlPDFHeaderStyles.police_logo} />
            {operationalArea && (
              <Text style={dlPDFHeaderStyles.operational_area}>
                {operationalArea}
              </Text>
            )}
          </View>
          <View style={dlPDFHeaderStyles.crime_types_container}>
            <Text style={dlPDFHeaderStyles.confidentiality_text}>
              <Link src="https://www.tbs-sct.canada.ca/pol/doc-eng.aspx?id=32614">
                *** {t(`bulletin/classification:${classification}`)} ***
              </Link>
            </Text>
            <Text
              style={{
                ...dlPDFHeaderStyles.crime_type,
                fontSize: type && type.length > 30 ? 18 : 22,
                textDecoration: resolvedResolution ? "line-through" : "none",
              }}
            >
              {t(`bulletin/bulletinType:${type}`)}
            </Text>
            <View style={dlPDFHeaderStyles.associated_crimes_container}>
              {associatedCrimes.map((crime, index) => (
                <Text style={dlPDFHeaderStyles.associated_crimes} key={index}>
                  {getCrimeLabel(crime.crime)}
                </Text>
              ))}
            </View>
            {incidentTimes.map((incidentTime, index) => (
              <Text
                style={dlPDFHeaderStyles.incident_text}
                key={index.toString()}
              >
                {incidentTimeText(incidentTime)}
              </Text>
            ))}
          </View>
          {resolvedResolution && (
            <View style={dlPDFHeaderStyles.resolved_stamp_container}>
              <Text style={dlPDFHeaderStyles.resolved_stamp}>
                {resolutionLabel(resolvedResolution)}
              </Text>
            </View>
          )}
        </View>
        {featuredImages.length > 0 && (
          <View style={dlPDFImageStyles.page_one_image_container}>
            {featuredImages.map((image, index) => (
              <View
                style={{
                  ...dlPDFImageStyles.image_card,
                  ...calculateImageDimensions(featuredImages.length),
                }}
                key={index.toString()}
              >
                <Image
                  src={evidenceBlobUrls.get(image.filename) ?? ""}
                  key={index}
                  style={dlPDFImageStyles.page_one_image}
                />
                <AttachmentTags
                  labels={attachmentLabels[image.filename]}
                  letter={attachmentLetters[image.filename]}
                  alertUrl={alertUrl}
                  isVideo={image.media_type === "video_thumbnail"}
                />
              </View>
            ))}
          </View>
        )}
        <View style={dlPDFDetailsStyles.details_container}>
          {hasEntities && (
            <PDFEntityList
              rows={entityRows}
              photos={photosByEntity}
              t={entityT}
            />
          )}
          {!hasEntities && tagDictValues.length > 0 && (
            <View style={dlPDFImageStyles.tag_description_container}>
              <View style={dlPDFImageStyles.tag_description_columns}>
                {tagDictWithDescription.map((tag, index) => (
                  <View
                    style={{
                      width: `${100 / tagDescriptionColumns}%`,
                      flexDirection: "column",
                      alignItems: "flex-start",
                      paddingTop: 8,
                      paddingBottom: 4,
                      paddingHorizontal: 4,
                      borderRight:
                        index % tagDescriptionColumns ===
                          tagDescriptionColumns - 1 ||
                        index === tagDictWithDescription.length - 1
                          ? ""
                          : "0.25px solid grey",
                      borderTop: "0.75px solid grey",
                    }}
                    key={index.toString()}
                    wrap={false}
                  >
                    <Text style={{ ...dlPDFImageStyles.tag }}>
                      {truncateTagTitle(tag.title, tagDescriptionTitleLength)}
                    </Text>
                    <Text>{tag.description}</Text>
                  </View>
                ))}
              </View>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginTop: 2,
                }}
              >
                {tagDictWithoutDescription.map((tag, index) => (
                  <Text
                    style={{ ...dlPDFImageStyles.tag, margin: 2 }}
                    key={index.toString()}
                  >
                    {truncateTagTitle(tag.title)}
                  </Text>
                ))}
              </View>
            </View>
          )}
          <View>{renderAddresses()}</View>

          <Text style={dlPDFDetailsStyles.description} widows={0}>
            {description}
          </Text>
          <Text style={dlPDFDetailsStyles.contact_label}>
            Anyone with information is asked to contact:
          </Text>
          {contacts.map((contact, index) => (
            <View
              style={dlPDFDetailsStyles.contact_details}
              key={index.toString()}
            >
              <Text>
                {contact.name}
                {contact?.badge_number && ` - ${contact.badge_number}`}
                {contact?.email && (
                  <>
                    {" "}
                    <Link
                      key={index}
                      style={dlPDFDetailsStyles.contact_text}
                      src={`mailto:${contact.email}`}
                    >
                      {contact.email}
                    </Link>
                  </>
                )}
                {contact?.phone_number && (
                  <>
                    {" "}
                    <Link
                      key={index}
                      style={dlPDFDetailsStyles.contact_text}
                      src={`tel:${stripPhoneNumberFormatting(
                        contact.phone_number,
                      )}`}
                    >
                      {formatPhoneNumber(contact.phone_number)}
                      {contact?.extension && ` ext. ${contact.extension}`}
                    </Link>
                  </>
                )}
              </Text>
            </View>
          ))}
          <Text style={dlPDFDetailsStyles.police_service_label}>
            {policeService?.name} - {occurrenceNumbers.join(", ")}
          </Text>

          <Text style={dlPDFDetailsStyles.approved_by_label}>
            {creatorToUse &&
              creatorText(creatorToUse) !== "" &&
              `Prepared by: ${creatorText(creatorToUse)}`}
            {approvedByText(approvedBy, approvedByBadge) !== "" &&
              ` Approved by: ${approvedByText(approvedBy, approvedByBadge)}`}
          </Text>
          <Text style={dlPDFDetailsStyles.issue_date}>
            Issued on: {issuedTime}
          </Text>
        </View>
        {additionalImages.length > 0 && (
          <>
            <Text
              style={dlPDFImageStyles.more_images_text}
              minPresenceAhead={ADDITIONAL_IMAGE_ROW_HEIGHT}
            >
              {t("bulletin:pdf_additional_images")}
            </Text>
            <View style={dlPDFImageStyles.image_container_remaining}>
              {additionalImages.map((image, index) => (
                <View
                  key={index.toString()}
                  style={dlPDFImageStyles.image_card_remaining}
                  wrap={false}
                >
                  <Image
                    src={evidenceBlobUrls.get(image.filename) ?? ""}
                    style={dlPDFImageStyles.image_remaining}
                  />
                  <AttachmentTags
                    labels={attachmentLabels[image.filename]}
                    letter={attachmentLetters[image.filename]}
                    alertUrl={alertUrl}
                    isVideo={image.media_type === "video_thumbnail"}
                  />
                </View>
              ))}
            </View>
          </>
        )}
        {publishedResolutions.length > 0 && (
          <View style={dlPDFResolutionStyles.resolutions_container} wrap>
            <Text style={dlPDFResolutionStyles.resolutions_heading}>
              {t("bulletin:pdf_updates_heading")}
            </Text>
            {publishedResolutions.map((resolution, index) => {
              const author = resolution.author;
              const authorName = author
                ? `${author.first_name || ""} ${author.last_name || ""}`.trim()
                : "";
              const resolutionImages = Object.values(
                resolution.attachments ?? {},
              ).filter(
                (attachment) =>
                  attachment.content_type?.includes("image") &&
                  resolutionBlobUrls.get(attachment.filename),
              );

              return (
                <View
                  key={index.toString()}
                  style={dlPDFResolutionStyles.resolution_card}
                  wrap={false}
                >
                  <View style={dlPDFResolutionStyles.resolution_header}>
                    <Text style={dlPDFResolutionStyles.resolution_reason}>
                      {resolutionLabel(resolution)}
                      {resolution.resolved &&
                        ` [${t("bulletin:resolve_closed").toUpperCase()}]`}
                    </Text>
                    <Text style={dlPDFResolutionStyles.resolution_date}>
                      {dayjs(resolution.time_updated).format("MMM D, YYYY")}
                    </Text>
                  </View>
                  {authorName !== "" && (
                    <Text style={dlPDFResolutionStyles.resolution_author}>
                      {t("bulletin:resolve_comment_by_prefix")} {authorName}
                      {author?.badge_number &&
                        ` (${t("bulletin:resolve_badge")}: ${author.badge_number})`}
                    </Text>
                  )}
                  {resolution.details !== "" && (
                    <Text style={dlPDFResolutionStyles.resolution_details}>
                      {resolution.details}
                    </Text>
                  )}
                  {resolutionImages.length > 0 && (
                    <View style={dlPDFResolutionStyles.resolution_images}>
                      {resolutionImages.map((attachment, imageIndex) => (
                        <Image
                          key={imageIndex.toString()}
                          src={
                            resolutionBlobUrls.get(attachment.filename) ?? ""
                          }
                          style={dlPDFResolutionStyles.resolution_image}
                        />
                      ))}
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}
        <View style={dlPDFFooterStyles.footer_container} fixed>
          <Text>
            Generated on: {currentDate} using{" "}
            <Link src="https://www.tryrubicon.com/">Rubicon</Link>{" "}
          </Text>
          <Text
            style={{
              position: "absolute",
              bottom: 10,
              left: 0,
              right: 0,
              textAlign: "center",
            }}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} / ${totalPages}`
            }
          />
          {alertId !== 0 && (
            <Text
              style={{
                position: "absolute",
                bottom: 10,
                left: "75%",
                right: 0,
                textAlign: "right",
                justifyContent: "flex-end",
              }}
            >
              Bulletin ID: {alertId}
            </Text>
          )}
        </View>
        <Text
          style={dlPDFFooterStyles.continued_hint}
          fixed
          render={({ pageNumber, totalPages }) =>
            pageNumber < totalPages ? t("bulletin:pdf_continued_next_page") : ""
          }
        />
      </Page>
    </Document>
  );

  return <MyDocument />;
}
